import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AccountSecurity } from '@/api'
import { SettingsRow, SettingsRowSkeleton } from '@/components/SettingsRow'
import type { LocalMethodProps } from '@/features/security/local/LocalSecuritySection'

import { AuthenticatorDialog, type AuthenticatorDialogState } from './AuthenticatorDialog'
import { useTotpMfa } from './useTotpMfa'

type AuthenticatorMethodProps = LocalMethodProps & {
    mfa: AccountSecurity['mfa'] | undefined
}

export const AuthenticatorMethod = ({ mfa, disabled, runAction }: AuthenticatorMethodProps) => {
    const { t } = useTranslation()
    const totp = useTotpMfa()
    const [dialog, setDialog] = useState<AuthenticatorDialogState | null>(null)

    if (!mfa) {
        return <SettingsRowSkeleton />
    }

    const enabled = mfa.enrolledFactors.includes('totp')

    if (!mfa.enabled || (!enabled && !mfa.availableFactors.includes('totp'))) {
        return null
    }

    const pending = totp.start.isPending || totp.verify.isPending || totp.remove.isPending

    const handleOpen = async () => {
        if (enabled) {
            setDialog({ mode: 'manage' })
            return
        }

        try {
            await runAction(async () => {
                const setup = await totp.start.mutateAsync()
                setDialog({ mode: 'setup', setup })
            })
        } catch {
            // The mutation displays setup errors in the account status banner.
        }
    }

    return (
        <>
            <SettingsRow
                icon={<DevicePhoneMobileIcon className="h-5 w-5" aria-hidden="true" />}
                title={t('security.local.authenticator.title')}
                description={t('security.local.authenticator.description')}
                status={enabled ? t('security.local.status.enabled') : t('security.local.status.notSetUp')}
                statusTone={enabled ? 'success' : 'neutral'}
                onAction={() => {
                    void handleOpen()
                }}
                disabled={disabled}
                pending={pending}
            />
            {dialog && (
                <AuthenticatorDialog
                    state={dialog}
                    onClose={() => setDialog(null)}
                    onVerify={(code) =>
                        runAction(async () => {
                            await totp.verify.mutateAsync(code)
                            setDialog(null)
                        })
                    }
                    onDisable={() =>
                        runAction(async () => {
                            await totp.remove.mutateAsync()
                            setDialog(null)
                        })
                    }
                />
            )}
        </>
    )
}
